import { useAuth } from 'lib/context';
import { pushToDataLayer } from 'lib/hooks/GTM/pushToDataLayer';
import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CovideoGTMVehicleItem,
  CovideoGTMVideoAttributes,
  GTMCDKUserAttributes,
  GTMEvent,
  GTMPageApplication,
  GTMPageviewAttributes,
  GTMVehicleAttributes,
  GTMVideoAttributes,
} from './types';
import { mapToGtmVehicleAttributes, mapToGTMVideoAttributes } from './utils';
import { mapToGTMMeetingAttributes } from './actions/meeting/utils';
import {
  CovideoGTMMeetingAttributes,
  GTMMeetingAttributes,
} from './actions/meeting/types';
import { useCDK } from '../useCdk';
import {
  CovideoGTMVideoMessageProperties,
  GTMVideoMessageProperties,
} from './actions/video';
import { mapToGtmVideoMessageProperties } from './actions/video/utils';

// partial/contains match for these
const STATIC_EXCLUDES = [
  '/send-and-share',
  '/classic/edit-video',
  '/voiceover',
  '/record',
  '/merge',
];

// exactly: /inventory/<one-segment> (not /inventory or /inventory/a/b)
const INVENTORY_ITEM_RX = /^\/inventory\/[^/]+(?:\/|$)/;
// exactly: /inventory/<one-segment> (not /inventory or /inventory/a/b)
const MEETINGS_ITEM_RX = /^\/meetings\/[^/]+(?:\/|$)/;

const PAGE_SECTION_MAP: Record<string, string> = {
  users: 'My Team',
  overview: 'My Stats',
  custom: 'Custom Reports',
  'activity-feed': 'Activity Feed',
  'landing-pages': 'Landing Pages',
  'service-pages': 'Sales Pages',
  'calls-to-action': 'Call-to-Actions',
  profile_edituserinfo: 'My Profile',
  profile_editcompanyinfo: 'Company Profile',
  profile_videoattributes: 'Custom Video Attributes',
  teleprompter: 'Teleprompter Scripts',
  'recently-deleted': 'Recently Deleted',
  details: 'Manage',
  trim: 'Trimming',
  home: 'Overview',
  meetings: 'Overview',
  guides: '',
  customers: '',
  inventory: 'Overview',
};

const PAGE_LOCATION_MAP: Record<string, string> = {
  classic: 'Library',
  home: 'Videos',
  guides: 'Training',
};

const capitalizeFirst = (str?: string): string | undefined =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : undefined;

const mapPageSection = (section: string | undefined): string | undefined => {
  if (!section) {
    return undefined;
  }
  // skip IDs
  if (!isNaN(Number(section))) {
    return undefined;
  }
  const key = section.toLowerCase();

  return PAGE_SECTION_MAP[key] === ''
    ? undefined
    : PAGE_SECTION_MAP[key] || capitalizeFirst(section);
};

const mapPageLocation = (location: string | undefined): string | undefined => {
  if (!location) {
    return undefined;
  }
  const key = location.toLowerCase();
  return PAGE_LOCATION_MAP[key] || capitalizeFirst(location);
};

export function useGTMTrackPageView() {
  const { userData } = useAuth();
  const { isCdkUser } = useCDK();
  const location = useLocation();
  const { pathname } = location;

  const segments = pathname.split('/').filter(Boolean);

  const autoLocationSlug = segments?.[0];

  let autoSectionSlug =
    segments.length > 0 ? segments[segments.length - 1] : undefined;

  if (autoSectionSlug && !isNaN(Number(autoSectionSlug))) {
    autoSectionSlug =
      segments.length > 1 ? segments[segments.length - 2] : undefined;
  }

  // Base attributes
  const autoPageSection = mapPageSection(autoSectionSlug);
  const autoPageLocation = mapPageLocation(autoLocationSlug);

  const cdkUserAttributes: GTMCDKUserAttributes = {
    user_id: userData?.eleadDMSEmployeeID || undefined,
    enterprise_id: userData?.customer.cdkEnterpriseId || undefined,
    store_id: userData?.customer.cdkStoreId || undefined,
    store_name: userData?.customer.business || undefined,
    is_internal_user: userData?.email?.includes('cdk'),
  };

  // actions
  const trackVideoSpecificPagesManually = useCallback(
    ({
      pageLocation,
      pageSection,
      videoAttributes,
      meetingAttributes,
      vehicle,
      mergeIds,
      videoMessageProperties,
    }: {
      pageLocation?: string;
      pageSection?: string;
      videoAttributes?: CovideoGTMVideoAttributes;
      meetingAttributes?: CovideoGTMMeetingAttributes;
      videoMessageProperties?: CovideoGTMVideoMessageProperties;
      vehicle?: CovideoGTMVehicleItem;
      mergeIds?: number[];
    }) => {
      const gtmMeetingAttributes: GTMMeetingAttributes =
        mapToGTMMeetingAttributes(meetingAttributes);

      const gtmVideoAttributes: GTMVideoAttributes =
        mapToGTMVideoAttributes(videoAttributes);

      const gtmVideoMessageProperties: GTMVideoMessageProperties =
        mapToGtmVideoMessageProperties(videoMessageProperties);

      const gtmVehicleAttributes: GTMVehicleAttributes =
        mapToGtmVehicleAttributes(vehicle as CovideoGTMVehicleItem);

      const pageViewAttributes: GTMPageviewAttributes = {
        event: GTMEvent.COVIDEO_PAGEVIEW,
        application: GTMPageApplication.CDK_VIDEO_WHITELABEL,
        page_url: window.location.href,
        page_location: pageLocation || autoPageLocation || undefined,
        page_section: pageSection || autoPageSection || undefined,
      };

      const payload = {
        ...pageViewAttributes,
        ...cdkUserAttributes,
        ...gtmVideoAttributes,
        ...gtmVehicleAttributes,
        ...gtmVideoMessageProperties,
        ...gtmMeetingAttributes,
        ...(mergeIds ? { merge_ids: mergeIds } : {}),
      };

      pushToDataLayer(payload);
    },
    [location.pathname, userData]
  );

  const trackPageViewAutomatically = useCallback(() => {
    const pageViewAttributes: GTMPageviewAttributes = {
      event: GTMEvent.COVIDEO_PAGEVIEW,
      application: GTMPageApplication.CDK_VIDEO_WHITELABEL,
      page_url: window.location.href,
      page_location: autoPageLocation || undefined,
      page_section: autoPageSection || undefined,
    };

    const payload = {
      ...pageViewAttributes,
      ...cdkUserAttributes,
    };

    pushToDataLayer(payload);
  }, [location.pathname, userData]);

  useEffect(() => {
    if (!userData) {
      return;
    }

    if (!isCdkUser) {
      return;
    }

    // Skip auto-track if path is in exclusion list
    const isExcluded =
      STATIC_EXCLUDES.some(s => pathname.includes(s)) ||
      INVENTORY_ITEM_RX.test(pathname) ||
      MEETINGS_ITEM_RX.test(pathname);

    if (isExcluded) {
      return;
    }

    trackPageViewAutomatically();
  }, [location.pathname, userData, trackVideoSpecificPagesManually]);

  return {
    trackVideoSpecificPagesManually: isCdkUser
      ? trackVideoSpecificPagesManually
      : undefined,
  };
}
